import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subscription } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { StreamService } from 'src/app/services/stream.service';

@Component({
    selector: 'app-record-conversation-video',
    templateUrl: './record-conversation-video.component.html',
    styleUrl: './record-conversation-video.component.scss',
    standalone: false
})
export class RecordConversationVideoComponent implements OnInit, OnDestroy {
	@Input() conversationID!: number;
	@Input() projectID!: number;
	@Output() startedRecording = new EventEmitter<boolean>();
	@Output() uploadComplete = new EventEmitter<void>();

  @ViewChild('videoPlayer') videoPlayer!: ElementRef<HTMLVideoElement>;
  @ViewChild('videoWrapper') videoWrapperEl!: ElementRef;
  @ViewChild('audioPlayer') audioPlayer!: ElementRef<HTMLAudioElement>;

	@ViewChild('startRecordingModal') startRecordingModal!: TemplateRef<any>;
	@ViewChild('voiceBaseModal') voiceBaseModal!: TemplateRef<any>;

	videoDevices: MediaDeviceInfo[] = [];
  audioInputDevices: MediaDeviceInfo[] = [];
  audioOutputDevices: MediaDeviceInfo[] = [];

  selectedCamera: string | null = null;
  selectedMicrophone: string | null = null;
  selectedSpeaker: string | null = null;

  muteMic = true;
  testSpeakers = false;
  audioMeterInterval: any;

	private videoStream!: MediaStream | undefined;
	private mediaRecorder!: MediaRecorder;

	passConditionsObserve!: Observable<boolean>;
	passConditionObservable!: Subscription;

	allowStartRecording = false;
	isRecording = false;

	showStopRecording = false;
	showVideoUploading = false;

	loading = false;
	recordingStartTime = 0;

	uploadInProgress = true;
	progressValue = 0;

	startRecordingModalRef: any;
	continueModalRef: any;

	baseline_start_time: any = null;
	baseline_end_time: any = null;

	speakers = [{ name: 'Single Speaker', value: 'y' }, { name: 'Multiple Speakers', value: 'n' }];
	speakerSelected: any = null;
	submitted = false;

	time = '00:00:00';
  intervalId: any; 

	constructor (private authService: AuthService, public streamService: StreamService, private modalService: NgbModal) {}

	ngOnInit() {
		this.passConditionsObserve = this.streamService.passConditions$;
		this.streamService.baseline.base_voice_start = null;
		this.streamService.baseline.base_voice_end = null;
		
		this.getMediaDevices();

		navigator.mediaDevices.ondevicechange = () => {
      this.getMediaDevices(); 
    };
  }

	ngOnDestroy(): void {
		setTimeout(() => {
			this.stopRecording();
		}, 3000);
	}

	getMediaDevices() {
    navigator.mediaDevices.getUserMedia({ audio: true, video: true })
		.then(stream => {
			navigator.mediaDevices.enumerateDevices()
				.then(devices => {
					this.videoDevices = devices.filter(device => device.kind === 'videoinput');
					this.audioInputDevices = devices.filter(device => device.kind === 'audioinput');
					this.audioOutputDevices = devices.filter(device => device.kind === 'audiooutput');
					

					if (!this.selectedCamera) {
						this.selectedCamera = this.videoDevices[0]?.deviceId || null;
						this.startVideoStream();
					}

					if (!this.selectedMicrophone) {
						this.selectedMicrophone = this.audioInputDevices[0]?.deviceId || null;
						this.startMicrophoneStream();
					}

					if (!this.selectedSpeaker) {
						this.selectedSpeaker = this.audioOutputDevices[0]?.deviceId || null;
					}

				});

			// Stop the stream immediately (not needed for device enumeration)
			stream.getTracks().forEach(track => track.stop());
		})
		.catch(error => console.error('Error accessing media devices:', error));
  }

	async startRecording() {
		this.streamService.conversation_id = this.conversationID;
		this.streamService.project_id = this.projectID;
		await this.streamService.updateConversationStatus(this.projectID, this.conversationID, this.speakerSelected);

		if (this.continueModalRef) {
			this.continueModalRef.close();
		}

		this.streamService.uploadProgressObs.subscribe((data) => {
			if (data.finishUploading) {
				this.uploadInProgress = false;

				setTimeout(() => {
					this.uploadComplete.emit();
				}, 3000);
			}

			this.progressValue = data.progress;
		});

		navigator.mediaDevices.getUserMedia({
			video: this.streamService.selectedCamera ? { deviceId: this.streamService.selectedCamera } : true,
			audio: this.streamService.selectedMicrophone ? { deviceId: this.streamService.selectedMicrophone } : true
		})
		.then(async (stream) => {
			if (!stream) {
				this.authService.toastErrorMessage('Video stream not available.', 'Error!');
				console.error('Video stream not available. Please grant access to media devices.');
				return;
			}
			
			this.videoStream = stream;
			this.mediaRecorder = new MediaRecorder(stream, {
				mimeType: 'video/webm; codecs=vp9',
				audioBitsPerSecond: 128000,
				videoBitsPerSecond : 4500000,
			});

			this.mediaRecorder.ondataavailable = (event: BlobEvent) => {
				if (event.data && event.data.size > 0) {
					this.streamService.storeChunk(event.data);
				}
			};

			this.mediaRecorder.onstart = () => {
				const videoElement = this.videoPlayer.nativeElement;
				this.recordingStartTime = videoElement.currentTime;

				this.streamService.baseline.baseline_start = 0;
				this.streamService.baseline.baseline_end = 30;
			};
	
			this.mediaRecorder.start(10000);
			let totalSeconds = 0;

			this.intervalId = setInterval(() => {
				totalSeconds++;
				this.time = this.formatTime(totalSeconds);
			}, 1000);
			
			this.isRecording = true;
			this.streamService.isRecording = true;
			this.streamService.processUploadQueue();

			this.streamService.checkConditions(this.videoPlayer.nativeElement);
		})
		.catch(error => console.error('Error accessing camera:', error));
	}

	start() {
		this.submitted = true;
		if (this.speakerSelected) {
			this.startRecordingModalRef = this.modalService.open(this.startRecordingModal, { size: 'xl', backdrop: 'static', keyboard : false });
		}
	}

	async stopRecording() {
		this.passConditionObservable.unsubscribe();
		clearInterval(this.intervalId);

    if (this.mediaRecorder) {
      this.mediaRecorder.stop();
    }

		if (this.videoStream) {
			this.videoStream.getTracks().forEach(track => track.stop());
    }
    
    if (this.videoPlayer && this.videoPlayer.nativeElement) {
			const videoElement = this.videoPlayer.nativeElement;
			const endTime = videoElement.currentTime;
			const minutes = Math.floor((endTime - this.recordingStartTime)/60);
			this.streamService.videoLength = minutes;

      this.videoPlayer.nativeElement.srcObject = null;
    }

		this.streamService.stopStreaming();
		this.isRecording = false;
		this.streamService.isRecording = false;
		this.showVideoUploading = true;
  }

	formatTime(totalSeconds: number): string {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return [hours, minutes, seconds].map(v => v < 10 ? '0' + v : v).join(':');
  }

	continue() {
		this.startRecordingModalRef.close();
		if (this.speakerSelected === 'n') {
			this.continueModalRef = this.modalService.open(this.voiceBaseModal, { size: 'xl', backdrop: 'static', keyboard : false });
		} else {
			this.startRecording();
		}
	}

	isMarked(mark: string) {
		switch (mark) {
			case 'v_start':
				return this.streamService.baseline.base_voice_start ? true : false;
			case 'v_end':
				return this.streamService.baseline.base_voice_end ? true : false;
		}

		return false;
	}

	markTime(mark: string) {
    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      const videoElement = this.videoPlayer.nativeElement;
      const currentTime = videoElement.currentTime;
      const elapsedRecordingTime = Math.floor(currentTime - this.recordingStartTime);

			switch (mark) {
				case 'v_start':
					this.streamService.baseline.base_voice_start = elapsedRecordingTime;
					break;
				case 'v_end':
					this.streamService.baseline.base_voice_end = elapsedRecordingTime;
					break;
				case 'reset_v':
					this.streamService.baseline.base_voice_start = null;
					this.streamService.baseline.base_voice_end = null;
					break;
			}
    }
  }

	async startVideoStream() {
    if (this.selectedCamera) {
      navigator.mediaDevices.getUserMedia({ video: { deviceId: this.selectedCamera } })
        .then(async (stream) => {
					this.videoStream = stream;
					this.streamService.selectedCamera = this.selectedCamera;

					this.videoPlayer.nativeElement.srcObject = this.videoStream;
					this.videoPlayer.nativeElement.style.objectFit = 'cover';
          this.checkFrameRate();
					await this.streamService.checkConditions(this.videoPlayer.nativeElement);

					this.passConditionObservable = this.passConditionsObserve.subscribe(pass => {
						this.allowStartRecording = pass;
					});
        })
        .catch(error => console.error('Error accessing camera:', error));
    }
  }

  startMicrophoneStream() {
    if (this.selectedMicrophone) {
      navigator.mediaDevices.getUserMedia({ audio: { deviceId: this.selectedMicrophone } })
			.then(stream => {
				this.videoStream = stream;
				this.streamService.selectedMicrophone = this.selectedMicrophone;

				const audioContext = new AudioContext();
				const analyser = audioContext.createAnalyser();
				const microphone = audioContext.createMediaStreamSource(this.videoStream);
				microphone.connect(analyser);

				this.audioMeterInterval = setInterval(() => {
					const bufferLength = analyser.frequencyBinCount;
					const dataArray = new Uint8Array(bufferLength);
					analyser.getByteFrequencyData(dataArray);
					this.updateAudioMeter(dataArray);
				}, 50); // Update every 50 milliseconds
			})
			.catch(error => console.error('Error accessing microphone:', error));
    }
  }

	updateAudioMeter(dataArray: Uint8Array) {
    const allPids = document.querySelectorAll('.pid');
    const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
    const numPidsToColor = Math.round(average / 10); // Adjust this for sensitivity

    allPids.forEach((pid, index) => {
      if (index < numPidsToColor) {
        pid.classList.add('bg-green');
      } else {
        pid.classList.remove('bg-green');
      }
    });
  }

	checkFrameRate() {
		if (this.videoPlayer.nativeElement.srcObject instanceof MediaStream) {
			const videoTracks = this.videoPlayer.nativeElement.srcObject.getVideoTracks();
	
			if (videoTracks && videoTracks.length > 0) { // Check if video tracks exist
				const videoTrack = videoTracks[0];
				const settings = videoTrack.getSettings();
				const actualFrameRate = settings.frameRate; // Safe to access now
				
				if (actualFrameRate !== undefined) { // Check if frameRate is defined
					this.streamService.frameRate = actualFrameRate;
					const minFrameRate = 25;
					this.allowStartRecording = actualFrameRate >= minFrameRate;
		
					if (actualFrameRate < minFrameRate) {
						console.error('Camera does not meet minimum frame rate requirement.');
						// You can display a more user-friendly error message here
					}
				} else {
					console.warn('Frame rate is undefined.');
					// You might want to handle this case specifically
				}
			} else {
				console.warn('No video tracks found.');
				// You can display a message to the user indicating no video is available
			}
		} else {
			console.warn('Video source is not a MediaStream, cannot check frame rate.');
		}
	}

	@HostListener('window:resize')
  resizeWindow() {
    const videoWidth = this.videoWrapperEl.nativeElement.offsetWidth;
    const videoHeight = videoWidth * 0.5625; // Maintain 16:9 aspect ratio
    this.videoPlayer.nativeElement.style.width = `${videoWidth}px`;
    this.videoPlayer.nativeElement.style.height = `${videoHeight}px`;
  }

  playsound() {
		if (this.selectedSpeaker) {
			if (this.testSpeakers) {
				this.audioPlayer.nativeElement.pause();
				this.testSpeakers = false;
			} else {
				this.testSpeakers = true;
				this.audioPlayer.nativeElement.currentTime = 0;
				this.audioPlayer.nativeElement.src = "./../../../assets/sound_check.mp3";
				this.audioPlayer.nativeElement.setSinkId(this.selectedSpeaker)
					.then(() => this.audioPlayer.nativeElement.play())
					.catch(error => console.error('Error setting audio output:', error));
			}
		}
  }

	muteStatusChange() {
    this.muteMic = !this.muteMic;
		this.audioPlayer.nativeElement.pause(); // Stop audio if mic is muted
		this.testSpeakers = false;
  }
}
